import { css } from 'styled-components'

const rem = {
  baseMobile: 15,
  base: 18,
  get: (value:number, factor = null) => `${value / (factor ?? rem.baseMobile)}rem`
}


// export const vwFont = (size: number, mobile: boolean = true) => rem.get(size, rem[mobile ? 'baseMobile' : 'base'])
// This version memoize outputs of vwFont() so it doesn’t recompute simple divisions on every render.
export const vwFont = (() => {
  const cache = new Map<string, string>()
  const MAX_CACHE_SIZE = 500 // arbitrary safe limit
  return (value: number, mobile = true) => {
    const key = `${value}-${mobile}`
    if (cache.has(key)) {
      return cache.get(key)!
    }
    const result = `${value / (mobile ? rem.baseMobile : rem.base)}rem`
    if (cache.size >= MAX_CACHE_SIZE) {
      cache.clear()
    }
    cache.set(key, result)
    return result
  }
})()


// NEW FONT
const getFontFamilyVarByKey = {
  clashgrotesk: '--font-clash-grotesk',
}

type FontSizeKeyword =
  | 'inherit' | 'initial' | 'unset'
  | 'xx-small' | 'x-small' | 'small' | 'medium' | 'large'
  | 'x-large' | 'xx-large' | 'xxx-large'
  | 'smaller' | 'larger'
type TypeFontSize = number | FontSizeKeyword | string
type TypeLineHeight = number | 'inherit' | 'initial' | 'unset' | string
type TypeFontUnits = 'px' | 'rem' | 'em' | '%' | 'ch' | 'vw' | 'vh' | 'vmin' | 'vmax'
type TypeFontWeight = 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 'lighter' | 'normal' | 'bold' | 'bolder'
type TypeFontStyle = 'normal' | 'italic' | 'oblique'
type TypeFontFamilyKey = keyof typeof getFontFamilyVarByKey

type TypeGetFont = {
  mobile?: boolean
  weight?: TypeFontWeight
  fontFamily?: TypeFontFamilyKey
  fontSize: TypeFontSize
  lineHeight?: TypeLineHeight
  fontSizeUnit?: TypeFontUnits
  lineHeightUnit?: TypeFontUnits
  fontStyle?: TypeFontStyle
  letterSpacing?: number
}

export const getFont = ({ mobile = false, weight = 400, letterSpacing, fontSize, fontSizeUnit = 'vw', lineHeight, lineHeightUnit = 'vw', fontStyle, fontFamily }:TypeGetFont) => css`
  font-size: ${typeof fontSize === 'number' ? fontSizeUnit === 'vw' ? vwFont(fontSize, mobile) : `${fontSize}${fontSizeUnit}` : fontSize};
  ${fontStyle !== undefined ? `font-style: ${fontStyle};` : ''}
  font-weight: ${weight};
  ${letterSpacing ? `letter-spacing: ${letterSpacing}px;` : ''}
  line-height: ${typeof lineHeight === 'number' ? lineHeightUnit === 'vw' ? vwFont(lineHeight, mobile) : `${lineHeight}${lineHeightUnit}` : lineHeight};
  ${fontFamily !== undefined ? `font-family: var(${getFontFamilyVarByKey[fontFamily]});` : ''}
`

type TypeFontProps = {
  fontWeight?: TypeFontWeight
  fontFamily?: TypeFontFamilyKey
  fontSize: TypeFontSize
  lineHeight?: TypeLineHeight
  fontSizeUnit?: TypeFontUnits
  lineHeightUnit?: TypeFontUnits
  fontStyle?: TypeFontStyle
  letterSpacing?: number
}

const breakpoints = {
  'watch': 180,
  'mobile-xs': 320,
  'mobile': 390,
  'nexus7': 603,
  'tablet': 768,
  'desktop-xs': 992,
  'desktop-md': 1200,
  'ipadpro-12': 1284,
  'desktop': 1440,
  'desktop-wide': 1650,
  'container': 1800,
  'desktop-large': 1920,
  'desktop-wqhd': 2560
} as const

type TypeBreakpointKey = keyof typeof breakpoints

const breakpointMinQueries = {
  mobile: '',
  tablet: `@media(min-width: ${breakpoints.tablet}px)`,
  desktop: `@media(min-width: ${breakpoints.desktop}px)`,
  'desktop-larget': `@media(min-width: ${breakpoints['desktop-large']}px)`
} as const

type TypeBreakpointMinQueries = keyof typeof breakpointMinQueries
type TypeResponsiveFont = Partial<Record<TypeBreakpointMinQueries, TypeFontProps>>

export const getFontAdvanced = (fonts: TypeResponsiveFont) => {
  const styles = (Object.keys(fonts) as TypeBreakpointMinQueries[]).map(breakpoint => {
    const font = fonts[breakpoint]
    if (!font) return ''

    const {
      fontSize,
      lineHeight,
      fontSizeUnit = 'vw',
      lineHeightUnit = 'vw',
      fontWeight = 400,
      letterSpacing,
      fontStyle,
      fontFamily
    } = font

    const fontSizeValue = typeof fontSize === 'number'
      ? fontSizeUnit === 'vw'
        ? vwFont(fontSize, breakpoint === 'mobile')
        : `${fontSize}${fontSizeUnit}`
      : fontSize

    const lineHeightValue = typeof lineHeight === 'number'
      ? lineHeightUnit === 'vw'
        ? vwFont(lineHeight, breakpoint === 'mobile')
        : `${lineHeight}${lineHeightUnit}`
      : lineHeight

    const output = `
      font-size: ${fontSizeValue};
      font-weight: ${fontWeight};
      ${fontStyle !== undefined ? `font-style: ${fontStyle};` : ''}
      ${letterSpacing !== undefined && letterSpacing !== 0 ? `letter-spacing: ${letterSpacing}px;` : ''}
      ${lineHeight !== undefined ? `line-height: ${lineHeightValue};` : ''}
      ${fontFamily !== undefined ? `font-family: var(${getFontFamilyVarByKey[fontFamily]});` : ''}
    `

    if (breakpoint === 'mobile') return output

    return `
      ${breakpointMinQueries[breakpoint]} {
        ${output}
      }
    `
  })

  return css`${styles}`
}


type TypeFontFluidProps = {
  fontSizeMin: TypeFontSize
  fontSizeMax: TypeFontSize
  fontSizeUnit?: TypeFontUnits
  lineHeightMin?: TypeLineHeight
  lineHeightMax?: TypeLineHeight
  lineHeightUnit?: TypeFontUnits
  fontWeight?: TypeFontWeight
  fontFamily?: TypeFontFamilyKey
  fontStyle?: TypeFontStyle
  letterSpacingMin?: number
  letterSpacingMax?: number
  viewportMin?: number | TypeBreakpointKey
  viewportMax?: number | TypeBreakpointKey
}

// Helper to calculate clamp()
function generateFluidClamp(
  fontSizeMin: number,
  fontSizeMax: number,
  viewportMin: number,
  viewportMax: number,
  unit: TypeFontUnits
) {
  const slope = (fontSizeMax - fontSizeMin) / (viewportMax - viewportMin) * 100
  const yAxisIntersection = fontSizeMin - (slope * viewportMin) / 100

  return `clamp(${fontSizeMin}${unit}, ${yAxisIntersection.toFixed(4)}${unit} + ${slope.toFixed(4)}vw, ${fontSizeMax}${unit})`
}

// Resolve a breakpoint value
function resolveBreakpoint(value: number | TypeBreakpointKey): number {
  if (typeof value === 'number') return value
  return breakpoints[value]
}

// Updated getFontFluid
export const getFontFluid = ({
  fontSizeMin,
  fontSizeMax,
  fontSizeUnit = 'px',
  lineHeightMin,
  lineHeightMax,
  lineHeightUnit = 'px',
  fontWeight = 400,
  fontFamily,
  fontStyle,
  letterSpacingMin,
  letterSpacingMax,
  viewportMin = 'mobile',
  viewportMax = 'desktop'
}: TypeFontFluidProps) => css`
  font-size: ${typeof fontSizeMin === 'number' && typeof fontSizeMax === 'number'
    ? generateFluidClamp(
      fontSizeMin,
      fontSizeMax,
      resolveBreakpoint(viewportMin),
      resolveBreakpoint(viewportMax),
      fontSizeUnit
    )
    : fontSizeMin};

  font-weight: ${fontWeight};
  ${fontStyle ? `font-style: ${fontStyle};` : ''}
  ${fontFamily ? `font-family: var(${getFontFamilyVarByKey[fontFamily]});` : ''}

  ${lineHeightMin !== undefined && lineHeightMax !== undefined ? `
    line-height: ${typeof lineHeightMin === 'number' && typeof lineHeightMax === 'number' ?
    generateFluidClamp(
      lineHeightMin,
      lineHeightMax,
      resolveBreakpoint(viewportMin),
      resolveBreakpoint(viewportMax),
      lineHeightUnit
    )
    : lineHeightMin};
  ` : ''}

  ${letterSpacingMin !== undefined && letterSpacingMax !== undefined ? `letter-spacing: ${
    generateFluidClamp(
      letterSpacingMin,
      letterSpacingMax,
      resolveBreakpoint(viewportMin),
      resolveBreakpoint(viewportMax),
      fontSizeUnit
    )};
  ` : ''}
`

export const presets = {
  font13_500: () => getFontAdvanced({
    mobile: {
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 13,
      letterSpacing: .65
    },
    tablet: {
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 13,
      letterSpacing: .65
    }
  }),
  font13_15: () => getFontAdvanced({
    mobile: {
      fontSize: 13,
      fontWeight: 400,
      lineHeight: 15.6,
    },
    tablet: {
      fontSize: 15,
      fontWeight: 400,
      lineHeight: 18,
    }
  }),
  font15: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 400,
      lineHeight: 15,
    }
  }),
  font15_20: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 400,
      lineHeight: 18,
    },
    tablet: {
      fontSize: 20,
      fontWeight: 400,
      lineHeight: 24,
    }
  }),
  font15_18: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 400,
      lineHeight: 15,
    },
    tablet: {
      fontSize: 18,
      fontWeight: 400,
      lineHeight: 18,
    }
  }),
  font15_seephotos: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 500,
      lineHeight: 17.25,
    },
  }),
  font15_height_16_5: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 400,
      lineHeight: 16.5,
    }
  }),
  font16_30: () => getFontAdvanced({
    mobile: {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: 16,
    },
    tablet: {
      fontSize: 30,
      fontWeight: 400,
      lineHeight: 30,
    }
  }),
  font18: () => getFontAdvanced({
    mobile: {
      fontSize: 18,
      fontWeight: 400,
      lineHeight: 20,
    },
    tablet: {
      fontSize: 18,
      fontWeight: 400,
      lineHeight: 20,
    }
  }),
  font18_20: () => getFontAdvanced({
    mobile: {
      fontSize: 18,
      fontWeight: 400,
      lineHeight: 21.6,
    },
    tablet: {
      fontSize: 20,
      fontWeight: 400,
      lineHeight: 24,
    }
  }),
  font25_1: () => getFontAdvanced({
    mobile: {
      fontSize: 25,
      fontWeight: 400,
      lineHeight: 28.75,
    },
  }),
  font25_2: () => getFontAdvanced({
    mobile: {
      fontSize: 25,
      fontWeight: 400,
      lineHeight: 30,
    },
  }),
  font26: () => getFontAdvanced({
    mobile: {
      fontSize: 26,
      fontWeight: 700,
      lineHeight: 30,
    },
    tablet: {
      fontSize: 26,
      fontWeight: 700,
      lineHeight: 30,
    }
  }),
  font30: () => getFontAdvanced({
    mobile: {
      fontSize: 25,
      fontWeight: 400,
      lineHeight: 27.5,
    },
    tablet: {
      fontSize: 30,
      fontWeight: 400,
      lineHeight: 33,
    }
  }),
  font30_35: () => getFontAdvanced({
    mobile: {
      fontSize: 30,
      fontWeight: 400,
      lineHeight: 30,
    },
    tablet: {
      fontSize: 35,
      fontWeight: 400,
      lineHeight: 35,
    }
  }),
  font40_60: () => getFontAdvanced({
    mobile: {
      fontSize: 40,
      fontWeight: 400,
      lineHeight: 40,
    },
    tablet: {
      fontSize: 60,
      fontWeight: 400,
      lineHeight: 60,
    }
  }),
  font50: () => getFontAdvanced({
    mobile: {
      fontSize: 30,
      fontWeight: 400,
      lineHeight: 30,
    },
    tablet: {
      fontSize: 50,
      fontWeight: 400,
      lineHeight: 50,
    }
  }),
  font141: () => getFontAdvanced({
    mobile: {
      fontSize: 75,
      fontWeight: 700,
      lineHeight: 64,
      letterSpacing: 1.56
    },
    tablet: {
      fontSize: 141,
      fontWeight: 700,
      lineHeight: 122,
      letterSpacing: 2.94
    }
  })
}


